import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ModalConfirmationContent from '../../../../shared/components/modal-confirmation-content/modal-confirmation-content';
import Modal from '../../../../shared/components/modal/modal';
import { useConfirmationError } from '../../../../shared/hooks/use-confirmation-error';
import { useAppSelector } from '../../../../store/hooks';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useRestoreSignRequestMutation } from '../../api/sign-requests-api';
import { signRequestAlertId, signRequestRestoreModalId } from '../../constants/constants';
import { selectTempSignRequestId, setTempSignRequestId } from '../../store/sign-requests-slice';

const SignRequestRestoreModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['sign-requests', 'common']);
  const dispatch = useDispatch();
  const activeSignRequestId = useAppSelector(selectTempSignRequestId);
  const { confirmationError, addConfirmationError, resetConfirmationError } = useConfirmationError();

  const [restoreSignRequest, { isLoading }] = useRestoreSignRequestMutation();

  const closeFormModal = () => {
    dispatch(closeModal(signRequestRestoreModalId));
    dispatch(setTempSignRequestId(null));
  };

  const onModalSubmit = () => {
    if (!activeSignRequestId) {
      addConfirmationError(t('signScanRequest.validationError.restoreFailed', { ns: 'common' }));
      return;
    }
    resetConfirmationError();

    restoreSignRequest(activeSignRequestId)
      .unwrap()
      .then(() => {
        closeFormModal();
        dispatch(
          setAlert({
            id: signRequestAlertId,
            type: 'success',
            title: t('signRequestRestoreModal.message.fileDeleted'),
          }),
        );
      })
      .catch(() => {
        addConfirmationError(t('signScanRequest.validationError.restoreFailed', { ns: 'common' }));
      });
  };

  return (
    <Modal
      title={t('signRequestRestoreModal.title')}
      onCancel={closeFormModal}
      isLoading={isLoading}
      customSubmitText={t('signScanRequest.action.restore', { ns: 'common' })}
      onOk={onModalSubmit}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      <ModalConfirmationContent
        confirmDeleteText={t('signScanRequest.hint.restoreWarning', { ns: 'common' })}
        confirmationError={confirmationError}
      />
    </Modal>
  );
};

const SignRequestRestoreModal = (): JSX.Element => {
  return <LazyModal modalId={signRequestRestoreModalId} Component={SignRequestRestoreModalContent} />;
};

export default SignRequestRestoreModal;
